import { memo, FC, useCallback, useMemo } from "react";
import pluralize from "pluralize";

import { Column, Row } from "@hightouch/htui";
import { LayoutGroup } from "motion/react";

import {
  FullParentModel,
  MinimalParentModel,
} from "src/components/audiences/types";
import type { StaleWarehouseConditionKind } from "src/components/audiences/utils";
import {
  Audience,
  AndCondition,
  AndOrCondition,
  Condition,
  ConditionType,
  FilterableColumn,
  OrCondition,
  RootCondition,
} from "src/types/visual";

import { QueryBuilderProvider } from "./context/query-builder-context";
import type { FilterRestrictions } from "./restrictions";
import { AndOrConditionField, ConditionField } from "./visual/condition";
import { Updater, UpdaterEnforceDefined } from "src/utils/immutable-helpers";
import { isAndOrCondition } from "./utils/type-guards";
import { DragTree } from "src/components/explore/draggable";
import { ConditionWrapperBarPartOnly } from "./visual/condition-wrapper";
import { GroupIndicatorBar } from "./visual/group-indicator-bar";
import {
  defaultConditionHeight,
  type QueryBuilderVariant,
} from "./visual/constants";
import {
  countLeafFilters,
  getDirectlyNestedAndOrConditionTypes,
} from "./visual/utils";

export type BaseQueryBuilderProps = {
  audience?: Audience;
  parentModel: FullParentModel | undefined | null;
  selectedEvent?: { eventModelId: number; relationshipId: number };
  enableReferencingEntryEvent?: boolean;
  /** Opt in to using realtime features */
  enableRealtime?: boolean;
  maxNestingDepth?: number;

  /** How densely condition rows render. Defaults to `"default"`, which
   * renders exactly as before — only surfaces that opt in change. `"compact"`
   * moves the AND/OR toggle inline into each condition's left rail, tightens
   * padding, hides the per-condition actions menu and disables drag. Applies to
   * root-level conditions only; see `isCompactCondition`. */
  variant?: QueryBuilderVariant;

  /** Filter features the consuming surface doesn't support. See
   * `FilterRestrictions` for the available features. */
  restrictions?: FilterRestrictions;

  /** Restrict the filter popover to parent-model Property conditions only.
   * Hides event, trait, relation, audience, and journey sections. Used by the
   * AID training matrix filter, which is evaluated on the training dataframe
   * and has no access to event streams. */
  propertiesOnly?: boolean;

  /** Override root property options. Used when the filter must target a derived feature surface. */
  filterableColumnsOverride?: FilterableColumn[];

  /** Defaults to true for the full query builder, false when using the QueryBuilderProvider directly */
  enableNestedFiltering?: boolean;

  /**
   * Live draft filter used to hydrate audience labels for SegmentSet conditions
   * referencing audiences outside the popover's paginated search window. Falls
   * back to the audience's saved filter when not provided.
   */
  visualQueryFilter?: { conditions?: unknown[] } | null;

  /**
   * Warehouse-relevant leaf conditions that have been added or modified
   * since the audience's last successful warehouse pull, keyed by id and
   * tagged with the kind of change. Used by the realtime audience UI to
   * surface per-condition staleness warnings.
   */
  staleWarehouseConditions?: Map<string, StaleWarehouseConditionKind>;
};

interface RenderPreviewProps {
  condition: Condition;
  /** This refers to the immediate parent condition, not the overall parent model */
  parentConditionType: ConditionType.And | ConditionType.Or;
  parent?: MinimalParentModel | null;
  audience?: Audience;
}

const noop = () => {};

const RenderPreview = (props: RenderPreviewProps) => {
  const { condition, parentConditionType, parent, audience } = props;
  const numLeafFilters = useMemo(
    () => countLeafFilters(condition),
    [condition],
  );
  const nestedAndOrTypes = useMemo(
    () => [
      parentConditionType,
      ...getDirectlyNestedAndOrConditionTypes(condition),
    ],
    [parentConditionType, condition],
  );
  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        minWidth: 0,
        borderRadius: "var(--chakra-radii-md)",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            backgroundColor: "white",
          }}
        >
          {condition.type === ConditionType.And ||
          condition.type === ConditionType.Or ? (
            <>
              <Row gap={4}>
                {nestedAndOrTypes.map((conditionType, index) => (
                  <GroupIndicatorBar
                    key={nestedAndOrTypes.slice(0, index + 1).join("-")}
                    conditionType={conditionType}
                  />
                ))}
              </Row>

              <Row
                minHeight={defaultConditionHeight}
                width="400px"
                borderBottomRightRadius="md"
                borderTopRightRadius="md"
                p={4}
                alignItems="center"
              >
                {pluralize("filter", numLeafFilters, true)}
              </Row>
            </>
          ) : (
            <ConditionWrapperBarPartOnly
              condition={condition}
              conditionType={parentConditionType}
              disableDrag
              forceDragStyles
            >
              <ConditionField
                condition={condition}
                audience={audience}
                parent={parent}
                onUpdate={noop}
                disableDrag
              />
            </ConditionWrapperBarPartOnly>
          )}
        </div>
      </div>
    </div>
  );
};

export type QueryBuilderProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  filter: AndOrCondition<AndCondition | OrCondition> | undefined;
  updateConditions: (
    updater: UpdaterEnforceDefined<RootCondition | undefined>,
    options?: { checkpoint?: boolean },
  ) => void;
  /** Render prop for an inline agent chat, shown in place of the Add filter button. */
  renderInlineAgent?: (props: {
    isOpen: boolean;
    onClose: () => void;
  }) => React.ReactNode;
  /** Render prop for root-level suggestion chips, rendered next to the Add filter button. */
  renderRootLevelSuggestions?: () => React.ReactNode;
} & BaseQueryBuilderProps;

const QueryBuilderUnmemoized: FC<Readonly<QueryBuilderProps>> = ({
  enableReferencingEntryEvent = false,
  isLoading = false,
  isDisabled = false,
  enableRealtime = false,
  enableNestedFiltering = true,
  audience,
  parentModel,
  filter = {
    type: ConditionType.And,
    conditions: [] as RootCondition[],
    id: crypto.randomUUID(),
  },
  selectedEvent,
  updateConditions,
  maxNestingDepth,
  variant,
  restrictions,
  propertiesOnly,
  filterableColumnsOverride,
  renderInlineAgent,
  renderRootLevelSuggestions,
  staleWarehouseConditions,
}) => {
  /** New immer-based update function */
  const updateFilter = useCallback(
    (
      updater: Updater<AndCondition | OrCondition>,
      { checkpoint = true }: { checkpoint?: boolean } = {},
    ) => {
      updateConditions(
        (rootConditionDraft) => {
          let draft = rootConditionDraft;
          if (!isAndOrCondition(draft)) {
            draft = { type: ConditionType.And, conditions: [] };
          }

          const result = updater(draft);
          if (!result || !isAndOrCondition(result)) {
            return draft;
          }
          // This type assertion could be removed by restricting `updater` to `AndCondition<RootCondition> | OrCondition<RootCondition>`
          // but that would require adding generics to AndOrConditionField which is probably more work than it's worth.
          return result as RootCondition;
        },
        { checkpoint },
      );
    },
    [updateConditions],
  );

  // Top-level AndOrCondition can't be removed
  const onRemove = useCallback(() => {
    updateFilter((filter) => {
      filter.conditions = [];
    });
  }, [updateFilter]);

  // It's best to hydrate ids higher up, immediately after data fetching to maximize stability,
  // but in case anything was missed, this is a fallback.
  const filterWithId = useMemo(() => {
    if (filter.id) return filter;
    return { ...filter, id: crypto.randomUUID() };
  }, [filter]);

  return (
    <QueryBuilderProvider
      audience={audience}
      parentModel={parentModel}
      selectedEvent={selectedEvent}
      enableReferencingEntryEvent={enableReferencingEntryEvent}
      enableNestedFiltering={enableNestedFiltering}
      enableRealtime={enableRealtime}
      maxNestingDepth={maxNestingDepth}
      variant={variant}
      visualQueryFilter={filterWithId}
      restrictions={restrictions}
      propertiesOnly={propertiesOnly}
      filterableColumnsOverride={filterableColumnsOverride}
      staleWarehouseConditions={staleWarehouseConditions}
    >
      <DragTree
        renderPreview={(item, dragListData) => (
          <RenderPreview
            condition={item as any}
            parentConditionType={dragListData as any}
            parent={parentModel}
            audience={audience}
          />
        )}
      >
        {/*
         * Without a stable condition id, framer motion has trouble connecting the dragged element through interactions
         * It's hard to truly fix without either injecting ids into the data or creating a parallel data structure with ids
         */}
        <LayoutGroup>
          <Column width="100%" fontSize={0}>
            <AndOrConditionField
              isLoading={isLoading}
              isDisabled={isDisabled}
              audience={audience}
              condition={filterWithId}
              level={0}
              parent={parentModel}
              onUpdate={updateFilter}
              onRemove={onRemove}
              renderInlineAgent={renderInlineAgent}
              renderRootLevelSuggestions={renderRootLevelSuggestions}
            />
          </Column>
        </LayoutGroup>
      </DragTree>
    </QueryBuilderProvider>
  );
};

export const QueryBuilder = memo(QueryBuilderUnmemoized);
